import React, {useState, useEffect, useCallback} from 'react';
import {Outlet, Link, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {loadUser, logoutUser} from '@store/reducers/auth';
import {toggleSidebarMenu} from '@app/store/reducers/ui';

import {addWindowClass, removeWindowClass, sleep} from '@app/utils/helpers';
import ControlSidebar from '@app/modules/main/control-sidebar/ControlSidebar';
import Header from '@app/modules/main/header/Header';
import MenuSidebar from '@app/modules/main/menu-sidebar/MenuSidebar';
import Footer from '@app/modules/main/footer/Footer';
import {PfImage} from '@profabric/react-components';
import {parseISO} from 'date-fns';
import {getAllUserNames} from '../../services/auth';
import * as AuthService from '../../services/auth';

const Main = () => {
  const dispatch = useDispatch();
  const menuSidebarCollapsed = useSelector(
    (state: any) => state.ui.menuSidebarCollapsed
  );

  const controlSidebarCollapsed = useSelector(
    (state: any) => state.ui.controlSidebarCollapsed
  );
  const screenSize = useSelector((state: any) => state.ui.screenSize);
  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [latestBday, setLatestBday] = useState<any>({});

  const handleToggleMenuSidebar = () => {
    dispatch(toggleSidebarMenu());
  };

  const handleStatus = (status: any) => {
    if (status === 401) {
      localStorage.removeItem('token');
      dispatch(logoutUser());
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await AuthService.getProfile();
      handleStatus(response.status);
      dispatch(loadUser(response.data));
      await sleep(1000);
      setIsAppLoaded(true);
    } catch (error) {
      dispatch(logoutUser());
      await sleep(1000);
      setIsAppLoaded(true);
    }
  };

  useEffect(() => {
    removeWindowClass('register-page');
    removeWindowClass('login-page');
    removeWindowClass('hold-transition');

    addWindowClass('sidebar-mini');

    fetchProfile();
    return () => {
      removeWindowClass('sidebar-mini');
    };
  }, []);

  useEffect(() => {
    removeWindowClass('sidebar-closed');
    removeWindowClass('sidebar-collapse');
    removeWindowClass('sidebar-open');
    if (menuSidebarCollapsed && screenSize === 'lg') {
      addWindowClass('sidebar-collapse');
    } else if (menuSidebarCollapsed && screenSize === 'xs') {
      addWindowClass('sidebar-open');
    } else if (!menuSidebarCollapsed && screenSize !== 'lg') {
      addWindowClass('sidebar-closed');
      addWindowClass('sidebar-collapse');
    }
  }, [screenSize, menuSidebarCollapsed]);

  useEffect(() => {
    if (controlSidebarCollapsed) {
      removeWindowClass('control-sidebar-slide-open');
    } else {
      addWindowClass('control-sidebar-slide-open');
    }
  }, [screenSize, controlSidebarCollapsed]);

  const getAppTemplate = useCallback(() => {
    if (!isAppLoaded) {
      return (
        <div className="preloader flex-column justify-content-center align-items-center">
          <PfImage
            className="animation__shake"
            src="/img/logo.png"
            alt="ArtzenWiseLogo"
            height={60}
            width={60}
          />
        </div>
      );
    }
    return (
      <>
        <Header />

        <MenuSidebar />

        <div className="content-wrapper">
          <Outlet />
        </div>
        <Footer />
        <ControlSidebar />
        <div
          id="sidebar-overlay"
          role="presentation"
          onClick={handleToggleMenuSidebar}
          onKeyDown={() => {}}
        />
      </>
    );
  }, [isAppLoaded]);

  const getUsersDetails = async () => {
    const resp = await getAllUserNames();
    if (resp?.status === 200) {
      setUsers(resp.data.data);
    }
  };

  const upcomingBirthdays = (users: any) => {
    const current = new Date().getTime();
    const nextDate = current + 1296000000;
    const filtered =
      users &&
      users.filter((user: any) => {
        const {dob} = user;
        if (!dob) {
          return false;
        }

        const splitted = dob.split('-');
        const modifiedDob = `${new Date().getFullYear()}-${splitted[1]}-${
          splitted[2]
        }`;

        const isValid = parseISO(modifiedDob);

        if (isValid.getDate() <= 31 && isValid.getMonth() + 1 <= 12 === true) {
          const newDate = new Date(modifiedDob).getTime();

          if (newDate >= current && newDate <= nextDate === true) {
            return user;
          }
        }
        return false;
      });

    const mapped =
      filtered &&
      filtered.map((user: any) => {
        const {dob} = user;

        const splitted = dob.split('-');
        const modifiedDob = `${new Date().getFullYear()}-${splitted[1]}-${
          splitted[2]
        }`;

        const latestDob = new Date(modifiedDob).getTime();
        return {...user, dob: latestDob};
      });

    const latestUpcoming = mapped.sort((a: any, b: any) => a.dob - b.dob);
    return latestUpcoming[0];
  };

  const commingFirstBirthDay = () => {
    setLatestBday(upcomingBirthdays(users));
  };

  const modifedDate = (dob: any) => {
    const all = new Date(dob);
    const date = all.getDate();
    const month = all.getMonth();
    const day = all.getDay();
    const week = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    return `${week[day]} ${date} ${months[month]}`;
  };

  useEffect(() => {
    getUsersDetails();
  }, []);

  useEffect(() => {
    if (users?.length > 0) {
      commingFirstBirthDay();
    }
  }, [users]);

  return (
    <>
      <div className="container-fluid bg-success">
        <div className="row">
          <div className="col-4 offset-4 justify-content-center text-center">
            {latestBday !== undefined ? (
              <p className="m-0 text-$white">
                {`${latestBday.name}'s Birthday on ${modifedDate(
                  latestBday.dob
                )} `}
              </p>
            ) : (
              <p className="m-0 text-$white">
                Please update your info by clicking{' '}
                <Link to="/profile" className="signup-btn">
                  here
                </Link>
              </p>
            )}{' '}
          </div>
        </div>
      </div>
      <div className="wrapper">{getAppTemplate()}</div>
    </>
  );
};

export default Main;
